// Global JS: shared utilities, deferred media, slider, and media pause logic
function algoliaStringify(value) {
  return value.trim().toLowerCase().replace(" ", "_").replace("/", "_");
}

// Object Id is the variant id
function addToAlgoliaStorage(productId, queryId, index) {
  const products = JSON.parse(localStorage.getItem('algoliaProducts')) || [];

  products.push({
    productId,
    queryId,
    index,
  });

  localStorage.setItem("algoliaProducts", JSON.stringify(products));
}

function handleize(input) {
  return input
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading/trailing whitespace
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Remove consecutive hyphens
}

Number.prototype.moneyWithoutCurrency = function () {
  return (this / 100).toFixed(2);
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return 'mobile';
  }
  return 'desktop';
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function JDRounding(value) {
  const intValue = Math.round(value);

  if (intValue <= 8) return 10;
  if (intValue >= 99) return 100;

  if (intValue >= 9 && intValue <= 13) return 10;
  if (intValue >= 14 && intValue <= 18) return 15;
  if (intValue >= 19 && intValue <= 23) return 20;
  if (intValue >= 24 && intValue <= 28) return 25;
  if (intValue >= 29 && intValue <= 33) return 30;
  if (intValue >= 34 && intValue <= 38) return 35;
  if (intValue >= 39 && intValue <= 43) return 40;
  if (intValue >= 44 && intValue <= 48) return 45;
  if (intValue >= 49 && intValue <= 53) return 50;
  if (intValue >= 54 && intValue <= 58) return 55;
  if (intValue >= 59 && intValue <= 63) return 60;
  if (intValue >= 64 && intValue <= 68) return 65;
  if (intValue >= 69 && intValue <= 73) return 70;
  if (intValue >= 74 && intValue <= 78) return 75;
  if (intValue >= 79 && intValue <= 83) return 80;
  if (intValue >= 84 && intValue <= 88) return 85;
  if (intValue >= 89 && intValue <= 93) return 90;
  if (intValue >= 94 && intValue <= 98) return 95;
  return intValue;
}

/**
 * General query function for the Storefront API. Accepts any query supported by Shopify.
 * https://shopify.dev/api/storefront/reference
 *
 * @param   {String} query  A graphQL query, you can test your query here: https://shopify.dev/graphiql/storefront-graphiql
 * @returns {JSON}          The JSON value returned by Shopify
 */
function graphQL(query) {
  return fetch(`https://${Shopify.shop}/api/2022-01/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/graphql',
      'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
    },
    body: query,
  }).then((res) => res.json());
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  static updateSourceFromDestination = (html, container, id) => {
    const source = html.getElementById(`${id}`);
    const destination = container.querySelector(`#${id}`);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;

      if (source.classList.contains('hidden')) {
        destination.classList.toggle('hidden');
      }

      if (source.tagName === 'BUTTON') {
        destination.disabled = source.disabled;
      }
    }
  };
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open')
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) {
        buttonMinus.classList.toggle(
          'disabled',
          parseInt(value) <= parseInt(this.input.min)
        );
      }
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('.drawer');
    this.mainDetailsToggle = this.querySelector('details');
    this.overlay = this.querySelector('.drawer__overlay');

    this.close = this.querySelectorAll('.drawer__close');
    this.drawerClose = this.querySelector('.drawer--close');

    this.addEventListener('keyup', this.onKeyUp.bind(this));

    if (this.mainDetailsToggle) {
      this.summary = this.mainDetailsToggle.querySelector('summary');
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (this.close.length > 0) {
      this.close.forEach(close => {
        close.addEventListener('click', this.closeMenuDrawer.bind(this));
      });
    }

    if (this.drawerClose) {
      this.drawerClose.addEventListener(
        'click',
        this.closeMenuDrawer.bind(this)
      );
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeMenuDrawer.bind(this));
    }
  }

  onSummaryClick() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.closeMenuDrawer(true);
    } else {
      this.openMenuDrawer();
    }

    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );
    }
  }

  open(target) {
    this.openMenuDrawer(target);
  }

  close() {
    this.closeMenuDrawer();
  }

  openMenuDrawer(target) {
    setTimeout(() => {
      this.drawer.classList.add('drawer--opened');
      document.body.classList.add('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      this.summary.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, this.summary);
    } else {
      trapFocus(this.drawer);

      if (target) {
        this.triggeringElement = target;
        this.triggeringElement.setAttribute('aria-expanded', true);
      }
    }
  }

  closeMenuDrawer(e) {
    if (e) {
      e.preventDefault();

      const closeButton = e.target.closest('.drawer__close');
      if (closeButton && closeButton.dataset.recursive == 'true') {
        const parentDrawer = this.parentNode.closest('menu-drawer');
        if (parentDrawer) {
          parentDrawer.closeMenuDrawer(e);
        }
      }
    }

    setTimeout(() => {
      this.drawer.classList.remove('drawer--opened');
      document.body.classList.remove('overflow-hidden');
    });

    if (this.mainDetailsToggle) {
      setTimeout(() => {
        this.mainDetailsToggle.removeAttribute('open');
      }, 200);

      this.summary.setAttribute('aria-expanded', false);
      removeTrapFocus(this.summary);
    } else {
      if (this.triggeringElement) {
        removeTrapFocus(this.triggeringElement);
        this.triggeringElement.setAttribute('aria-expanded', false);
      } else {
        removeTrapFocus();
      }
    }

    document.body.classList.remove('overflow-hidden');
  }

  onKeyUp(event) {
    if (!event || !event.code || event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    this.closeMenuDrawer();
  }
}

customElements.define('menu-drawer', MenuDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.removeOnClose = this.dataset.removeOnClose === 'true';

    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if(!event) return;
      if(!event.code) return;
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  open(opener) {
    this.show(opener);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    const media = this.querySelector('deferred-media');

    if (media) {
      media.loadContent();
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    if (this.removeOnClose) {
      this.remove();
    }
  }

  close() {
    this.hide();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.posterButton = this.querySelector('.media__poster');
    this.posterImage = this.querySelector('.media__poster-image');

    const autoplay = this.dataset.autoplay === 'true';

    if (this.posterButton) {
      this.posterButton
        .addEventListener('click', (e) => {
          e.preventDefault();

          this.loadContent();
        })
        .bind(this);
    }

    if (this.posterImage) {
      this.posterImage
        .addEventListener('click', (e) => {
          e.preventDefault();

          this.loadContent();
        })
        .bind(this);
    }
  
    // Track current breakpoint bucket so device switch can swap media.
    this.viewportBucket = window.innerWidth < 750 ? 'mobile' : 'desktop';
  
    this._onEditorResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.handleEditorResize();
      }, 120);
    };

    window.addEventListener('resize', this._onEditorResize);

    if (autoplay) {
      this.loadContent(false);
    }
  }

  disconnectedCallback() {
    if (this._onEditorResize) {
      window.removeEventListener('resize', this._onEditorResize);
    }
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
    }
  }
  
  handleEditorResize() {
    const nextBucket = window.innerWidth < 750 ? 'mobile' : 'desktop';
    if (nextBucket === this.viewportBucket) return;
  
    this.viewportBucket = nextBucket;
  
    // Only rehydrate when media is already loaded.
    if (!this.hasAttribute('loaded')) return;
  
    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }
  
    // Remove already-injected media nodes and let loadContent pick the right one.
    this.querySelectorAll(':scope > video, :scope > model-viewer, :scope > iframe')
      .forEach((node) => node.remove());
  
    this.removeAttribute('loaded');
    this.loadContent(false);
  }

  loadContent(focus = true) {
    //window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );

      const children = content.querySelectorAll('video, model-viewer, iframe');

      this.setAttribute('loaded', true);

      children.forEach((child, index) => {
        if (
          child.classList.contains('hidden--mobile') &&
          window.innerWidth < 750
        ) {
          return;
        }

        if (
          child.classList.contains('hidden--desktop') &&
          window.innerWidth >= 750
        ) {
          return;
        }

        const deferredElement = this.appendChild(child);

        if (focus) deferredElement.focus();

        if (
          deferredElement.nodeName == 'VIDEO' &&
          deferredElement.getAttribute('autoplay')
        ) {
          // force autoplay for safari
          deferredElement.play();
        }
      });

      if (this.posterButton) {
        this.posterButton.remove();
      }

      if (this.posterImage) {
        this.posterImage.remove();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const waitForInit = this.dataset.waitForInit == 'true';

    if (!waitForInit) {
      this.init();
    }
  }

  init() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.loop = this.dataset.loop == 'true';
    this.autoplay = this.dataset.autoplay == 'true';
    this.speed = this.dataset.speed || 5000;
    this.forcePaused = this.dataset.forcePaused == 'true';
    this.liftDots = this.dataset.liftDots == 'true';

    this.slider = this.querySelector(':scope > [id^="Slider-"]');
    this.buttons = this.querySelector(':scope > .slider-buttons');

    if (!this.slider) return;

    if (this.buttons) {
      this.prevButton = this.buttons.querySelector('button[name="prev"]');
      this.nextButton = this.buttons.querySelector('button[name="next"]');
      this.dots = this.buttons.querySelector('.slider-counter--dots');
      this.scrollbar = this.buttons.querySelector('.slider-scrollbar');

      if (this.prevButton) {
        this.prevButton.addEventListener(
          'click',
          this.onButtonClick.bind(this)
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          this.onButtonClick.bind(this)
        );
      }
    }

    if (this.autoplay) {
      this.setAutoPlay();
    }

    const debouncedOnScroll = debounce((event) => {
      this.currentSlide = this.getCurrentSlideFromPosition();
      this.currentPage = this.getCurrentPage();

      this.setButtonsState();
      this.setDotsState();
    }, 50);

    this.slider.addEventListener('scroll', debouncedOnScroll.bind(this));

    const resizeObserver = new ResizeObserver((entries) => this.setupPages());
    resizeObserver.observe(this.slider);

    this.setupPages();

    // Opt-in "snap-scroll" mode: on mobile only, swipes act as discrete
    // next/prev gestures with no mid-swipe peeking. Enabled via the
    // [data-snap-scroll="true"] attribute on <slider-component>.
    if (this.dataset.snapScroll === 'true') {
      this.snapScrollMQ = window.matchMedia('(max-width: 749px)');
      this.boundSnapMQChange = this.onSnapScrollMQChange.bind(this);
      this.snapScrollMQ.addEventListener('change', this.boundSnapMQChange);
      this.onSnapScrollMQChange();
    }
  }

  onSnapScrollMQChange() {
    if (this.snapScrollMQ.matches) {
      this.enableSnapScroll();
    } else {
      this.disableSnapScroll();
    }
  }

  enableSnapScroll() {
    if (this.snapScrollEnabled) return;
    this.snapScrollEnabled = true;

    // The CSS hook applies touch-action: pan-y (blocks native horizontal
    // pan so the slider doesn't move as the finger drags) and forces
    // scroll-behavior: auto (so setPage's scrollLeft change is instant).
    this.slider.setAttribute('data-snap-scroll-active', 'true');

    // Touch events (not pointer events) — iOS Safari is buggy with
    // Pointer Events on elements that use touch-action: pan-y; horizontal
    // gestures often skip pointerdown/up entirely. Touch events fire
    // reliably on iOS.
    this.boundSnapTouchStart = this.onSnapTouchStart.bind(this);
    this.boundSnapTouchMove = this.onSnapTouchMove.bind(this);
    this.boundSnapTouchEnd = this.onSnapTouchEnd.bind(this);

    this.slider.addEventListener('touchstart', this.boundSnapTouchStart, { passive: true });
    this.slider.addEventListener('touchmove', this.boundSnapTouchMove, { passive: true });
    this.slider.addEventListener('touchend', this.boundSnapTouchEnd);
    this.slider.addEventListener('touchcancel', this.boundSnapTouchEnd);
  }

  disableSnapScroll() {
    if (!this.snapScrollEnabled) return;
    this.snapScrollEnabled = false;

    this.slider.removeAttribute('data-snap-scroll-active');
    this.slider.removeEventListener('touchstart', this.boundSnapTouchStart);
    this.slider.removeEventListener('touchmove', this.boundSnapTouchMove);
    this.slider.removeEventListener('touchend', this.boundSnapTouchEnd);
    this.slider.removeEventListener('touchcancel', this.boundSnapTouchEnd);
    this.snapTracking = false;
  }

  onSnapTouchStart(event) {
    // Ignore multi-touch (pinches etc.)
    if (event.touches.length !== 1) {
      this.snapTracking = false;
      return;
    }

    const touch = event.touches[0];
    this.snapTracking = true;
    this.snapStartX = touch.clientX;
    this.snapStartY = touch.clientY;
    this.snapLastX = touch.clientX;
  }

  onSnapTouchMove(event) {
    if (!this.snapTracking) return;
    if (event.touches.length !== 1) {
      this.snapTracking = false;
      return;
    }
    // Track the latest X so touchend/touchcancel can use a fresh value.
    this.snapLastX = event.touches[0].clientX;
  }

  onSnapTouchEnd() {
    if (!this.snapTracking) return;
    this.snapTracking = false;

    const deltaX = this.snapLastX - this.snapStartX;
    const deltaY = (this.snapLastY ?? this.snapStartY) - this.snapStartY;

    // Threshold: 20% of slider width or 80px, whichever is smaller. Tight
    // enough that a confident flick always counts, loose enough that an
    // accidental short drag doesn't change slides.
    const threshold = Math.min(this.slider.clientWidth * 0.2, 80);
    // Ignore primarily-vertical drags (they should just have scrolled the
    // page natively via touch-action: pan-y).
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (Math.abs(deltaX) <= threshold) return;

    const totalPages = Math.ceil(this.totalPages);
    if (deltaX < 0) {
      const next = Math.min(totalPages - 1, this.currentPage + 1);
      if (next !== this.currentPage) this.setPage(next);
    } else {
      const prev = Math.max(0, this.currentPage - 1);
      if (prev !== this.currentPage) this.setPage(prev);
    }
  }

  getCurrentSlideFromPosition() {
    return Math.floor(this.slider.scrollLeft / this.sliderItemWidth);
  }

  getCurrentPage() {
    return (
      Math.ceil(Math.abs(this.currentSlide) / this.slidesPerPage) %
      Math.ceil(this.totalPages)
    );
  }

  setupPages() {
    /*
     * Only check for visible slides since some may be hidden based on screen sizes.
     */
    this.sliderItems = this.slider.querySelectorAll(':scope > [id^="Slide-"]');

    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    /*
     * If there are less than 2 visible slides, return.
     */
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemsToShow.forEach((slide, index) => {
      slide.setAttribute('data-slide-index', index);
    });

    /**
     * Assume that each slide is the same width. Get the width of the first visible slide.
     *
     * Use this width to calculate the number of slides per page.
     */
    this.sliderItemWidth = this.sliderItemsToShow[0].offsetWidth;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemWidth
    );

    /**
     * Calculate the total number of pages.
     *
     * If there are is only 1 page, hide the buttons.
     */
    this.totalSlides = this.sliderItemsToShow.length;
    this.totalPages = this.totalSlides / this.slidesPerPage;

    // TODO: Eventually allow for custom slide counts per page.
    this.step = this.slidesPerPage; //Math.ceil(this.slidesPerPage / this.totalPages);

    this.currentSlide = this.getCurrentSlideFromPosition();
    this.currentPage = Math.ceil(this.currentSlide / this.slidesPerPage);

    if (this.buttons) {
      if (this.totalPages <= 1) {
        this.buttons.classList.add('hidden');
      } else {
        this.buttons.classList.remove('hidden');
      }
    }

    if (this.loop) {
      this.nextSlideItemsToShow = this.sliderItemsToShow.slice();

      this.prevSlideItemsToShow = this.sliderItemsToShow.slice();
      this.prevSlideItemsToShow.reverse();

      this.maxSlides = this.totalSlides;
      this.minSlides = 0;
    }

    if(this.liftDots && this.scrollbar) {
      // Get size of `.multicolumn-card__container`, and set the scrollbar bottom to the size of the content + 24px
      const contentHeight = this.querySelector('.multicolumn-card__container').offsetHeight;
      this.scrollbar.style.bottom = (contentHeight + 24) + 'px';
    } 

    /**
     * Generate the dots if they exist.
     */
    this.generateDots();
    this.generateScrollbar();
    this.setButtonsState();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll(':scope > [id^="Slide-"]');
    this.setupPages();
  }

  generateDots() {
    if (this.dots) {
      this.dots.innerHTML = '';

      for (let i = 0; i < this.totalPages; i++) {
        const dot = document.createElement('button');

        dot.classList.add('slider-counter__link');
        dot.classList.add('slider-counter__link--bars');
        dot.classList.add('link');

        if (i == 0) {
          dot.classList.add('slider-counter__link--active');
        }

        dot.setAttribute('data-slide-to', i);
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.setAttribute('aria-controls', this.slider.id);

        dot.addEventListener('click', (event) => {
          const slideTo = event.currentTarget.getAttribute('data-slide-to');
          this.setPage(slideTo);
        });

        const span = document.createElement('span');
        span.classList.add('dot');

        dot.appendChild(span);

        this.dots.appendChild(dot);
      }
    }
  }

  generateScrollbar() {
    if (this.scrollbar) {
      if(this.totalPages <= 1) {
        this.scrollbar.classList.add('hidden');
      } else {
        this.scrollbar.classList.remove('hidden');
      }

      this.scrollbar.setAttribute('data-current-page', this.currentPage);
      this.scrollbar.setAttribute('data-total-pages', Math.floor(this.totalPages));

      // Expose page count as a CSS variable so the snap-scroll gradient
      // (track segments + gaps) can compute against it without enumerating.
      this.scrollbar.style.setProperty('--total-pages', Math.ceil(this.totalPages));

      this.scrollbarThumb = this.scrollbar.querySelector('.slider-scrollbar__thumb');

      // In snap-scroll mode the thumb must visually fit inside a single
      // segment of the gapped track. Otherwise it overhangs into the 15px
      // gap between pages and looks broken.
      if (this.dataset.snapScroll === 'true') {
        this.scrollbarThumb.style.width = `calc(${100 / Math.ceil(this.totalPages)}% - 15px)`;
      } else {
        this.scrollbarThumb.style.width = (100 / Math.ceil(this.totalPages)) + '%';
      }

      if (typeof this.scrollbar.init === 'function') {
        this.scrollbar.init();
      } else {
        customElements.whenDefined('slider-scrollbar').then(() => {
          this.scrollbar.init();
        });
      }
    }
  }

  update() {
    if (
      !this.slider ||
      !this.nextButton ||
      this.currentSlide == this.previousSlide
    ) {
      return;
    }

    this.currentPage =
      Math.ceil(Math.abs(this.currentSlide) / this.slidesPerPage) %
      Math.ceil(this.totalPages);

    if (this.loop) {
      /**
       * Handles infinite loop when navigating past the first or last slide.
       * Creates cloned slides at the beginning to maintain seamless scrolling.
       *
       * Steps:
       * 1. Extract slides to clone from their respective arrays
       * 2. Clone the DOM elements for these slides
       * 3. Mark clones with 'slider__slide--clone' class
       * 4. Set proper data-slide-index for each clone
       * 5. Insert clones at the beginning or end of the slider
       */

      if (this.currentSlide < this.minSlides) {
        // 1.
        const slidesToAdd = this.prevSlideItemsToShow.slice(0, this.step);
        this.prevSlideItemsToShow = this.prevSlideItemsToShow
          .slice(this.step)
          .concat(slidesToAdd);

        // 2.
        const clonedSlides = slidesToAdd.map((slide) => slide.cloneNode(true));

        // 3.
        clonedSlides.forEach((slide) =>
          slide.classList.add('slider__slide--clone')
        );

        // 4.
        clonedSlides.forEach((slide, index) => {
          slide.setAttribute(
            'data-slide-index',
            (this.previousSlide || 0) - index - 1
          );
        });

        // 5.
        this.slider.prepend(...clonedSlides.reverse());

        this.minSlides = this.currentSlide;
      } else if (this.currentSlide + this.slidesPerPage > this.maxSlides) {
        // 1.
        const slidesToAdd = this.nextSlideItemsToShow.slice(0, this.step);
        this.nextSlideItemsToShow = this.nextSlideItemsToShow
          .slice(this.step)
          .concat(slidesToAdd);

        // 2.
        const clonedSlides = slidesToAdd.map((slide) => slide.cloneNode(true));

        // 3.
        clonedSlides.forEach((slide) =>
          slide.classList.add('slider__slide--clone')
        );

        // 4.
        clonedSlides.forEach((slide, index) => {
          slide.setAttribute('data-slide-index', this.maxSlides + index);
        });

        // 5.
        this.slider.append(...clonedSlides);

        this.maxSlides = this.maxSlides + this.step;
      }
    } else {
      this.setButtonsState();
    }

    if (this.dots) {
      this.setDotsState();
    }

    const slideToShow = this.slider.querySelector(
      `[data-slide-index="${this.currentSlide}"]`
    );
    if (slideToShow) {
      this.slider.scrollLeft = slideToShow.offsetLeft;
    }

    this.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentSlide],
        },
      })
    );

    this.previousSlide = this.currentSlide;
  }

  setDotsState() {
    if (this.dots) {
      this.dots
        .querySelectorAll('.slider-counter__link')
        .forEach((dot, index) => {
          dot.classList.remove('slider-counter__link--active');
          if (index == this.currentPage) {
            dot.classList.add('slider-counter__link--active');
          }
        });
    }
  }

  setButtonsState() {
    if (!this.loop) {
      if (this.prevButton) {
        if (this.currentPage < 1) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
      }

      if (this.nextButton) {
        if (this.currentPage >= this.totalPages - 1) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const direction = event.currentTarget.name;

    if (this.loop) {
      if (direction == 'next') {
        this.currentSlide += this.step;
      } else if (direction == 'prev') {
        this.currentSlide -= this.step;
      }
    } else {
      if (direction == 'next') {
        this.currentSlide += this.step;

        if (this.currentSlide >= this.totalSlides) {
          this.currentSlide = this.totalSlides - 1;
        }
      } else if (direction == 'prev') {
        this.currentSlide -= this.step;

        if (this.currentSlide < 0) {
          this.currentSlide = 0;
        }
      }
    }

    this.update();
  }

  setSlidePosition(slidePosition) {
    this.currentSlide = slidePosition;
    this.update();
  }

  setPage(page) {
    this.currentSlide = page * this.slidesPerPage;
    this.update();
  }

  setAutoPlay() {
    this.slider.setAttribute('aria-live', 'polite');
    this.slider.setAttribute('aria-atomic', 'true');

    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.sliderAutoplayButton = this.querySelector('.slider-button__toggle');

    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(this)
      );

      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  autoPlayToggle() {
    const videos = this.querySelectorAll('video');

    if (this.forcePaused) {
      this.play();
      this.forcePaused = false;

      this.sliderAutoplayButton.setAttribute('data-pause', 'false');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow
      );

      videos.forEach((video) => video.play());
    } else {
      this.pause();
      this.forcePaused = true;

      this.sliderAutoplayButton.setAttribute('data-pause', 'true');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow
      );

      videos.forEach((video) => video.pause());
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');

    clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(
      this.autoRotateSlides.bind(this),
      this.speed
    );
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplayInterval);
  }

  autoRotateSlides() {
    this.currentSlide += this.step;

    if (this.currentSlide >= this.totalSlides) {
      this.currentSlide = 0;
    }

    this.update();
  }

  focusOutHandling(event) {
    if (!this.forcePaused) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (!this.forcePaused) {
      this.pause();
    }
  }
}

customElements.define('slider-component', SliderComponent);


class SliderScrollbar extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector('.slider-scrollbar__track');
    this.thumb = this.querySelector('.slider-scrollbar__thumb');

    this.dragging = false;
    this.dragStartClientX = 0;
    this.dragStartScrollLeft = 0;
  }

  // Called by SliderComponent.generateScrollbar(). May be invoked multiple
  // times (on resize / re-layout) — we tear down previous listeners via
  // AbortController so we don't accumulate handlers.
  init() {
    const parent = this.closest('slider-component');
    this.slider = parent?.slider;
    if (!this.slider || !this.track || !this.thumb) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Slider scroll → reflect into thumb position. Throttled to one update
    // per frame to keep things smooth on fast trackpads. Also temporarily
    // disables scroll-snap so wheel/trackpad/touch scrolling doesn't fight
    // the snap points — snap is restored a moment after the user stops.
    this.slider.addEventListener('scroll', () => {
      if (!this.dragging) this.disableSnapDuringScroll();

      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        if (!this.dragging) this.updateThumb();
      });
    }, { signal });

    // Click anywhere on the track (but not the thumb itself) → scroll to
    // that position smoothly. Centers the thumb under the cursor.
    this.track.addEventListener('click', (event) => {
      if (event.target === this.thumb) return;
      const trackRect = this.track.getBoundingClientRect();
      const clickX = event.clientX - trackRect.left;
      this.scrollToThumbLeft(clickX - this.thumb.offsetWidth / 2, true);
    }, { signal });

    // Drag the thumb. Pointer events cover mouse / touch / pen uniformly.
    this.thumb.addEventListener('pointerdown', this.onPointerDown.bind(this), { signal });

    this.updateThumb();
  }

  // How far the thumb can travel across the track. Normally that's
  // (trackWidth - thumbWidth) — thumb starts at 0, ends with its right
  // edge at the track's right edge.
  //
  // In snap-scroll mode the track has 15px transparent gaps between N
  // segments and the thumb is exactly one segment wide. There the thumb
  // should land at the START of each segment, so travel must equal
  // (N-1) full segment widths — otherwise the thumb overshoots by 15px
  // per page on its way to the right edge.
  getThumbTravel() {
    if (this.slider.hasAttribute('data-snap-scroll-active')) {
      const totalPages = Number(this.style.getPropertyValue('--total-pages')) || 1;
      return (this.track.clientWidth * (totalPages - 1)) / totalPages;
    }
    return this.track.clientWidth - this.thumb.offsetWidth;
  }

  // Translate the thumb to match the slider's current scroll position. We
  // compute a 0-1 ratio of how far through the scrollable distance we are,
  // then position the thumb across the available track travel.
  updateThumb() {
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    if (maxScroll <= 0) {
      this.thumb.style.transform = 'translateX(0)';
      return;
    }
    const ratio = Math.max(0, Math.min(1, this.slider.scrollLeft / maxScroll));
    const travel = this.getThumbTravel();
    this.thumb.style.transform = `translateX(${ratio * travel}px)`;
  }

  // Disable scroll-snap while a scroll interaction is in progress, and
  // restore it ~150ms after the last scroll event. This keeps wheel/touch
  // scrolling smooth instead of snapping on every frame, while still
  // letting the slider snap to the nearest slide when the user stops.
  disableSnapDuringScroll() {
    if (!this.snapManaged) {
      this.snapManaged = true;
      this.previousSnapType = this.slider.style.scrollSnapType || '';
      this.slider.style.scrollSnapType = 'none';
    }

    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.slider.style.scrollSnapType = this.previousSnapType;
      this.snapManaged = false;
    }, 150);
  }

  // Move the slider so the thumb's left edge lands at `targetThumbLeft`
  // (clamped within the track). Smooth-scrolls if requested.
  scrollToThumbLeft(targetThumbLeft, smooth = false) {
    const travel = this.getThumbTravel();
    if (travel <= 0) return;
    const clamped = Math.max(0, Math.min(travel, targetThumbLeft));
    const ratio = clamped / travel;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    this.slider.scrollTo({
      left: ratio * maxScroll,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }

  onPointerDown(event) {
    event.preventDefault();

    this.dragging = true;
    this.activePointerId = event.pointerId;
    this.dragStartClientX = event.clientX;

    // Capture the thumb's current X so we can translate from that point
    // forward, not from 0. Reading from the actual transform avoids drift
    // when re-grabbing the thumb mid-page.
    const matrix = new DOMMatrixReadOnly(getComputedStyle(this.thumb).transform);
    this.dragStartThumbX = matrix.m41 || 0;

    // scroll-snap-type fights us during a drag — every scrollLeft mutation
    // would snap to the nearest snap point and the thumb would jitter.
    this.previousSnapType = this.slider.style.scrollSnapType;
    this.slider.style.scrollSnapType = 'none';

    // Listen on document so we keep getting events regardless of where the
    // pointer ends up. setPointerCapture is unreliable when the pointer
    // crosses elements with different touch-action values.
    this.boundMove = this.onPointerMove.bind(this);
    this.boundUp = this.onPointerUp.bind(this);
    document.addEventListener('pointermove', this.boundMove);
    document.addEventListener('pointerup', this.boundUp);
    document.addEventListener('pointercancel', this.boundUp);
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.activePointerId) return;
    event.preventDefault();

    const travel = this.getThumbTravel();
    if (travel <= 0) return;

    // Drive the thumb POSITION directly from the pointer's pixel delta.
    // We don't read scrollLeft back — that round-trip is where jitter comes
    // from (browsers can return slightly different values than what we set,
    // sub-pixel rounding, scroll constraints, etc.).
    const deltaX = event.clientX - this.dragStartClientX;
    const newThumbX = Math.max(0, Math.min(travel, this.dragStartThumbX + deltaX));

    // Paint the thumb immediately at the new position.
    this.thumb.style.transform = `translateX(${newThumbX}px)`;

    // Mirror the position into the slider as a side effect. Use scrollTo
    // with behavior: 'instant' to bypass the slider's CSS scroll-behavior:
    // smooth — on mobile that smooth-scroll animates every scrollLeft
    // mutation over ~200ms, so during drag the slider perpetually lags
    // behind the thumb and the thumb appears to "reset" on release. The
    // slider's scroll listener won't double-update the thumb because the
    // dragging flag is on.
    const ratio = newThumbX / travel;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    this.slider.scrollTo({ left: ratio * maxScroll, behavior: 'instant' });
  }

  onPointerUp(event) {
    if (event.pointerId !== this.activePointerId) return;

    this.dragging = false;
    this.activePointerId = null;

    document.removeEventListener('pointermove', this.boundMove);
    document.removeEventListener('pointerup', this.boundUp);
    document.removeEventListener('pointercancel', this.boundUp);

    // Restore scroll-snap. The browser will smoothly snap to the nearest
    // snap point from wherever we ended up.
    this.slider.style.scrollSnapType = this.previousSnapType || '';
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

customElements.define('slider-scrollbar', SliderScrollbar);


class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.colorSwatches = this.querySelectorAll('.product-form__color');

    this.sizeContainer = this.querySelector(
      '.product-form__input--size,.product-form__input--taille'
    );
    this.sizeSwatches = this.querySelectorAll(
      '.product-form__input--size .product-form__pill,.product-form__input--taille'
    );

    if (this.colorSwatches) {
      this.colorSwatches.forEach((swatch) => {
        swatch.addEventListener('mouseover', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          const value = swatch.getAttribute('data-value');

          this.selectedValue = label.innerHTML;
          label.innerHTML = value;
        });

        swatch.addEventListener('mouseout', () => {
          const parent = swatch.closest('.product-form__input');
          const label = parent.querySelector('.product-form__label');

          label.innerHTML = this.selectedValue;
        });
      });
    }

    if (this.sizeSwatches) {
      this.sortSizeSwatches();
    }

    if (this.sizeContainer) {
      this.sizeContainer.classList.add('size-swatches-sorted');
    }
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }

  sortSizeSwatches() {
    if (
      !window.theme?.size_order_list ||
      !Array.isArray(window.theme.size_order_list)
    ) {
      return;
    }

    // Convert NodeList to Array for easier manipulation
    const swatchArray = Array.from(this.sizeSwatches);

    // Get the parent container
    const container = swatchArray[0]?.parentElement;
    if (!container) return;

    // Sort the swatches based on the size_order_list
    const sortedSwatches = swatchArray.sort((a, b) => {
      const sizeA = a.getAttribute('data-value');
      const sizeB = b.getAttribute('data-value');

      const indexA = window.theme.size_order_list.indexOf(sizeA);
      const indexB = window.theme.size_order_list.indexOf(sizeB);

      // If size is not found in order list, put it at the end
      const orderA =
        indexA === -1 ? window.theme.size_order_list.length : indexA;
      const orderB =
        indexB === -1 ? window.theme.size_order_list.length : indexB;

      return orderA - orderB;
    });

    // Update the DOM by appending sorted elements
    sortedSwatches.forEach((swatch) => {
      container.appendChild(swatch);
    });

    // Update the sizeSwatches NodeList reference
    this.sizeSwatches = this.querySelectorAll(
      '.product-form__input--size .product-form__pill'
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations();
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    fetch(`${this.dataset.url}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        const googleAnalyticsProducts = [];
        recommendations
          .querySelectorAll('.grid__item')
          .forEach((item, index) => {
            googleAnalyticsProducts.push({
              item_id: item.dataset.id,
              item_name: item.dataset.title,
              affiliation: Shopify.shop,
              index: index,
              item_brand: item.dataset.vendor,
              item_category: item.dataset.type,
              item_list_id: 'Related Products',
              item_list_name: 'Related Products',
              price: item.dataset.price,
              quantity: 1,
            });
          });

        const dataLayerObj = {
          event: 'view_item_list',
          page_location: window.location.href,
          page_referrer: document.referrer,
          page_title: document.title,
          ecommerce: {
            item_list_id: 'Related Products',
            item_list_name: 'Related Products',
            items: googleAnalyticsProducts,
          },
        };

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(dataLayerObj);
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class HoverComponent extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');

    this.addEventListener('mouseover', this.handleMouseOver.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    this.summary.addEventListener('click', this.handleSummaryClick.bind(this));
  }

  handleSummaryClick(e) {
    if (e) {
    e.preventDefault();
    }
    return false;
  }

  handleMouseOver() {
    this.details.setAttribute('open', '');
  }

  handleMouseLeave() {
    this.details.removeAttribute('open');
  }
}

customElements.define('hover-component', HoverComponent);




class QuickaddContainer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', this.handleVariantChange.bind(this));
  }

  connectedCallback() {
    this.dispatchEvent(new CustomEvent('quickadd:loaded', { bubbles: true }));
  }

  handleVariantChange(event) {
    if (!this.contains(event.target)) return;

    const target = event.target;

    if(window.innerWidth < 750) {
      this.querySelector('product-form').onSubmitHandler();

      target.checked = false;
      this.classList.remove('card__quickadd--open');
    }
    else {
      if (window.theme.dynamic_yield_enabled && DynamicYield) {
        DynamicYield.changeAttribute(target.dataset.optionName, target.dataset.optionValue);
      }
  
      const url = this.buildRequestUrl(
        this.dataset.productUrl,
        target.value
      );
  
      this.renderProductInfo({
        requestUrl: url,
        callback: this.handleUpdateProductInfo(),
      });
    }    
  }

  buildRequestUrl(url, variantId) {
    const params = [];

    params.push(`section_id=card`);

    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    return `${url}?${params.join('&')}`;
  }

  renderProductInfo({ requestUrl, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        callback(html);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  handleUpdateProductInfo() {
    return (html) => {
      const variant = this.getSelectedVariant(html);

      HTMLUpdateUtility.updateSourceFromDestination(
        html,
        this,
        `QuickaddForm-${this.dataset.productId}`
      );

      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          productId: this.dataset.productId,
          html,
          variant,
        },
      });
    };
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector(
      'product-form .quickadd__variant-checkbox[checked]'
    )?.dataset.sku;
    return selectedVariant || null;
  }
}

if (!customElements.get('quickadd-container')) {
  customElements.define('quickadd-container', QuickaddContainer);
}

class QuickaddTrigger extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    event.preventDefault();

    this.closest('quickadd-container').classList.toggle('card__quickadd--open');
  }
}

if (!customElements.get('quickadd-trigger')) {
  customElements.define('quickadd-trigger', QuickaddTrigger);
}


class KlaviyoForm extends HTMLElement {
  constructor() {
    super();

    const source = this.dataset.source;
    const form = this.querySelector('form');

    if (!form) {
      return;
    }

    KlaviyoSubscribe.attachToForms(`#${form.id}`, {
      hide_form_on_success: true,
      success: function (form) {
        var email = form[0].querySelector('[type="email"]');

        if (window.theme.dynamic_yield_enabled && DynamicYield) {
          DynamicYield.newsletterSubscription(email.value);
        }

        const string = `
            <div class='signup-form__success'>
              <h3>${window.theme.klaviyo_success.title}</h3>
              <p>${window.theme.klaviyo_success.message}</p>
            </div>
          `;

        form[0].closest('klaviyo-form').parentElement.innerHTML = string;
      },
      error: function (form) {
        console.error('error');
      },
      success_message: window.theme.klaviyo_success.message,
      extra_properties: {
        $source: source,
      },
    });
  }
}

customElements.define('klaviyo-form', KlaviyoForm);

class TabPanel extends HTMLElement {
  constructor() {
    super();
    this.tabButtonsContainer = this.querySelector('.tab-buttons-container');
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    this.initTabs();
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });
  }

  selectTab(selectedTab) {
    this.panels.forEach((panel) => (panel.style.display = 'none'));
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', 'false'));

    const panel = this.querySelector(
      `#${selectedTab.getAttribute('aria-controls')}`
    );
    panel.style.display = 'block';
    selectedTab.setAttribute('aria-selected', 'true');
    selectedTab.focus();
  }

  onKeydown(event, tab) {
    const key = event.key;
    let newTab;

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      newTab = tab.previousElementSibling || this.tabs[this.tabs.length - 1];
    } else if (key === 'ArrowRight' || key === 'ArrowDown') {
      newTab = tab.nextElementSibling || this.tabs[0];
    } else if (key === 'Home') {
      newTab = this.tabs[0];
    } else if (key === 'End') {
      newTab = this.tabs[this.tabs.length - 1];
    }

    if (newTab) {
      event.preventDefault();
      this.selectTab(newTab);
    }
  }
}

customElements.define('tab-panel', TabPanel);

// A card counting down to a coming_soon_date replaces itself with a freshly
// rendered one when the date lands. setTimeout coerces its delay to a signed
// 32-bit int, so waits past ~24.8 days fire immediately rather than late and
// get chunked instead. The jitter staggers a grid full of cards from the same
// drop, which would otherwise all fetch on the very same tick.
const CARD_COMING_SOON_MAX_DELAY = 2147483647;
const CARD_COMING_SOON_RETRY_DELAY = 10000;
const CARD_COMING_SOON_GRACE_PERIOD = 300000;
const CARD_COMING_SOON_JITTER = 10000;

class CardComponent extends HTMLElement {
  constructor() {
    super();

    this.isDy = this.dataset.isDy === 'true';

    this.comingSoonJitter = Math.round(
      Math.random() * CARD_COMING_SOON_JITTER
    );

    if (window.innerWidth > 750) {
      this.addEventListener('mouseover', this.handleMouseOver.bind(this));
      this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }
    else {
      /*
      this.currentAction = 'over';
      setInterval(() => {
        if (this.currentAction === 'over') {
          this.handleMouseOver();
          this.currentAction = 'leave';
        }
        else {
          this.handleMouseLeave();
          this.currentAction = 'over';
        }
      }, 9000);
      */
    }

    /*
    if (this.isDy) {
      this.imageWrapper = this.querySelector('.card__image-wrapper');

      this.price = parseInt(this.dataset.price);
      this.compareAtPrice = this.dataset.compareAtPrice;      

      if (this.price < this.compareAtPrice) {
        const percentOff = ((this.compareAtPrice - this.price) / this.compareAtPrice) * 100;

        const div = document.createElement('div');
        div.classList.add('badge', 'badge--percent-off', 'card__price-badge');
        div.innerHTML = `<rounding-component data-value="${percentOff}"></rounding-component>
            ${window.cards.percent_off}`;

        this.imageWrapper.append(div);
      }
    }
    */
  }

  connectedCallback() {
    this.scheduleRefresh();
  }

  disconnectedCallback() {
    clearTimeout(this.refreshTimeout);
    this.refreshAbortController?.abort();
  }

  get releaseAt() {
    const seconds = parseInt(this.dataset.availableAt, 10);

    return Number.isNaN(seconds)
      ? null
      : seconds * 1000 + this.comingSoonJitter;
  }

  scheduleRefresh(retryDelay) {
    clearTimeout(this.refreshTimeout);

    const releaseAt = this.releaseAt;
    if (releaseAt === null) return;

    let delay = releaseAt - Date.now();

    if (retryDelay !== undefined) {
      // The first attempt always runs, however late the visitor lands — their
      // page may be cached from before the drop while the section renders
      // correctly. Retries are what get bounded.
      if (delay < -CARD_COMING_SOON_GRACE_PERIOD) {
        console.warn(
          '[card-component] Still coming soon well past the release date; giving up.',
          this
        );
        return;
      }

      delay = retryDelay + this.comingSoonJitter;
    }

    if (delay <= 0) {
      this.refresh();
      return;
    }

    // Re-entering through scheduleRefresh() rather than calling refresh() from
    // the timeout re-reads the clock, so a chunked wait picks up where it left
    // off and a machine that slept mid-wait still settles on the right moment.
    this.refreshTimeout = setTimeout(
      () => this.scheduleRefresh(),
      Math.min(delay, CARD_COMING_SOON_MAX_DELAY)
    );
  }

  async refresh() {
    const { handle, variantId } = this.dataset;
    if (!handle) return;

    this.refreshAbortController?.abort();
    this.refreshAbortController = new AbortController();

    // Whether the section renders as "coming soon" depends on the clock, so a
    // plain URL can be served from a CDN entry built before the drop. Bucketing
    // by the minute bounds how stale that can be while still letting everyone
    // arriving in the same minute share one origin render.
    const params = new URLSearchParams({ section_id: 'card' });
    if (variantId) params.set('variant', variantId);
    params.set('_ts', Math.floor(Date.now() / 60000));

    try {
      const response = await fetch(`/products/${handle}?${params}`, {
        signal: this.refreshAbortController.signal,
      });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const source = html.querySelector('card-component');
      if (!source) throw new Error('card-component not found in response');

      // Still counting down to the same date means a stale cache — swapping it
      // in would land us right back here. A different date means the drop
      // moved, so swap and let the new card schedule itself.
      if (source.dataset.availableAt === this.dataset.availableAt) {
        this.scheduleRefresh(CARD_COMING_SOON_RETRY_DELAY);
        return;
      }

      // sections/card.liquid renders standalone, without the layout arguments
      // the grid passed this card, so its classes are carried over rather than
      // taken from the response.
      source.className = this.className;

      const wrapper = document.createElement('div');
      HTMLUpdateUtility.setInnerHTML(wrapper, source.outerHTML);

      this.replaceWith(wrapper.firstElementChild);
    } catch (e) {
      if (e.name === 'AbortError') return;

      console.error('[card-component] Failed to refresh card:', e);
      this.scheduleRefresh(CARD_COMING_SOON_RETRY_DELAY);
    }
  }

  handleMouseOver() {
    const slider = this.querySelector('.slider');

    if (slider) {
      const slides = slider.querySelectorAll('.slider__slide');
      const slideWidth = slides[0].clientWidth;

      slider.scrollTo({
        left: slideWidth,
        behavior: 'instant',
      });
    }
  }

  handleMouseLeave() {
    const slider = this.querySelector('.slider');

    if (slider) {
      slider.scrollTo({
        left: 0,
        behavior: 'instant',
      });
    }
  }
}

customElements.define('card-component', CardComponent);

class RoundingComponent extends HTMLElement {
  constructor() {
    super();

    this.value = this.dataset.value;

    const roundedValue = JDRounding(parseFloat(this.value));
    this.innerHTML = `${roundedValue}`;
  }
}

customElements.define('rounding-component', RoundingComponent);

class PriceComponent extends HTMLElement {
  constructor() {
    super();

    this.price = parseFloat(this.dataset.price);
    this.compareAtPrice = this.dataset.compareAtPrice;

    if(this.compareAtPrice) {
      this.compareAtPrice = parseFloat(this.compareAtPrice);
    }
    else {
      this.compareAtPrice = 0;
    }

    if (this.compareAtPrice > this.price) {
      this.classList.add('price--on-sale');
    }

    this.innerHTML = `
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Sale Price</span>
        <span class="price-item">
          $${this.price}
        </span>
      </div>            

      ${
        this.compareAtPrice > this.price
          ? `
        <div class="price__regular body body--small gray">
          <span class="visually-hidden visually-hidden--inline">Regular Price</span>
          <s class="price-item">
            $${this.compareAtPrice}
          </s>
        </div>
      `
          : ''
      }
    `;
  }
}

customElements.define('price-component', PriceComponent);




class InventoryContainer extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.inventory__form');
    this.finder = this.querySelector('.inventory__finder-button');

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.finder.addEventListener('click', this.handleFinderClick.bind(this));

    this.storeOrder = localStorage.getItem('storeOrder');

    if(this.storeOrder) {
      this.storeOrder = JSON.parse(this.storeOrder);

      this.setStoreOrder();
    }
  }

  handleFinderClick(event) {
    event.preventDefault();

    if (navigator.geolocation) {
      console.log("navigator.geolocation is supported");
      navigator.geolocation.getCurrentPosition(this.handleFinderSuccess.bind(this), this.handleFinderError.bind(this));
    } else {
      console.log("navigator.geolocation is not supported");
      this.setClassState("permission_error");
    }
  }

  handleFinderSuccess(position) {
    this.setClassState("loading");
    const { latitude, longitude } = position.coords;
    this.reorderStores(longitude, latitude);
    this.setStoreOrder();
  }

  handleFinderError(error) {
    this.setClassState("permission_error");
  }

  async handleSubmit(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const location = formData.get('location');

    this.setClassState("loading");
    this.classList.remove('inventory--expanded');

    const locationResponse = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${location}&key=AIzaSyDWlCC7oZbUQ1XkXcjzD5EiuzXnx88P_6c`
    );
    const locationData = await locationResponse.json();

    if(locationData.results.length > 0) {
      const longitude = locationData.results[0].geometry.location.lng;
      const latitude = locationData.results[0].geometry.location.lat;

      this.reorderStores(longitude, latitude);
      this.setStoreOrder();
    }
    else {
      this.setClassState("error");
      return;
    }
  }

  reorderStores(userLongitude, userLatitude) {
    const storeItems = Array.from(this.querySelectorAll('.inventory__result-item'));
    const storeOrder = storeItems.map((storeItem) => {
      const distance = this.calculateDistance(
        userLongitude,
        userLatitude,
        parseFloat(storeItem.dataset.storeLongitude),
        parseFloat(storeItem.dataset.storeLatitude)
      );

      return {
        storeNumber: storeItem.dataset.storeNumber,
        distance,
      };
    });

    storeOrder.sort((a, b) => a.distance - b.distance);

    this.storeOrder = storeOrder;
    localStorage.setItem('storeOrder', JSON.stringify(storeOrder));
  }

  setStoreOrder() {
    const storeItems = Array.from(this.querySelectorAll('.inventory__result-item'));
    const storeOrder = this.storeOrder || JSON.parse(localStorage.getItem('storeOrder'));

    if(!storeItems.length || !storeOrder || !storeOrder.length) {
      return;
    }

    const orderLookup = new Map(
      storeOrder.map((store, index) => [store.storeNumber?.toString() ?? '', index])
    );

    storeItems.sort((a, b) => {
      const aIndex = orderLookup.has(a.dataset.storeNumber)
        ? orderLookup.get(a.dataset.storeNumber)
        : Number.MAX_SAFE_INTEGER;
      const bIndex = orderLookup.has(b.dataset.storeNumber)
        ? orderLookup.get(b.dataset.storeNumber)
        : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });

    const listContainer = storeItems[0].parentElement;

    if(listContainer.querySelector(".inventory__result-more")) {
      listContainer.querySelector(".inventory__result-more").remove();
    }

    storeItems.forEach((storeItem) => { 
      const distance = storeOrder.find((item) => item.storeNumber === storeItem.dataset.storeNumber)?.distance;
      storeItem.querySelector('.inventory__store-distance').innerHTML = `${typeof distance == "number" ? `${distance} ${window.language.distance_away}` : 'N/A'}`;
      listContainer.appendChild(storeItem);
    });

    const moreButton = document.createElement('li');
    moreButton.classList.add('inventory__result-more');
    moreButton.innerHTML = `
      <button class="inventory__result-more-button button">
        ${window.language.view_more_stores}
      </button>
      <p class="inventory__result-more-warning body">
        ${window.language.view_more_warning}
      </p>
    `;

    listContainer.appendChild(moreButton);

    moreButton.addEventListener('click', () => {
      this.showMoreStores();
    });

    this.setClassState("loaded");
  }

  showMoreStores() {
    this.classList.add('inventory--expanded');
  }

  calculateDistance(userLongitude, userLatitude, longitude, latitude) {	
    const earthRadiusMeters = 6371e3;
    const storeLatitudeRadians = latitude * Math.PI / 180;
    const userLatitudeRadians = userLatitude * Math.PI / 180;
    const latitudeDifferenceRadians = (userLatitude - latitude) * Math.PI / 180;
    const longitudeDifferenceRadians = (userLongitude - longitude) * Math.PI / 180;
    
    const haversine =
      Math.sin(latitudeDifferenceRadians / 2) * Math.sin(latitudeDifferenceRadians / 2) +
      Math.cos(storeLatitudeRadians) * Math.cos(userLatitudeRadians) *
      Math.sin(longitudeDifferenceRadians / 2) * Math.sin(longitudeDifferenceRadians / 2);
    const centralAngle = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    
    const distanceInMeters = earthRadiusMeters * centralAngle;
    const distanceInKM = distanceInMeters / 1000;

    return parseInt(distanceInKM);
  }  

  setClassState(state) {
    this.dataset.state = state;
  }
}

customElements.define('inventory-container', InventoryContainer);




if (!customElements.get('inventory-toggle')) {
  customElements.define(
    'inventory-toggle',
    class InventoryToggle extends HTMLElement {

      constructor() {
        super();  

        this.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(event) {
        event.preventDefault();

        this.button = this.querySelector('button');

        if(!this.button) {
          return;
        }

        this.productHandle = this.button.dataset.productHandle;
        this.variantId = this.button.dataset.variantId;        

        fetch(`/products/${this.productHandle}?variant=${this.variantId}&section_id=product_availability`)
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
           
            const oldAvailabilityDialog = document.querySelector('#AvailabilityDialog');
            if(oldAvailabilityDialog) {
              oldAvailabilityDialog.remove();
            }            

            const newAvailabilityDialog = html.querySelector('#AvailabilityDialog');
            document.body.appendChild(newAvailabilityDialog);

            newAvailabilityDialog.open(this.button);
          });
      }
    }
  );
}




// Custom "Coming Soon" / Back-in-Stock implementation backed by Klaviyo's
// client-side Back-in-Stock Subscription endpoint.
//
//   <coming-soon-trigger> — wraps the "Coming Soon" button in buy-buttons (or
//     wherever Algolia drops it). On click, fetches the product_coming-soon
//     section HTML scoped to the current variant, swaps it into the DOM,
//     and opens the modal. Mirrors the <inventory-toggle> pattern above.
//
//     It also counts down to [data-available-at] and re-renders the whole
//     <product-form> in place once the release date lands, so a visitor
//     sitting on the PDP at drop time gets a working Add to Cart without
//     reloading. Pages are CDN cached, so the section can come back still
//     rendering "coming soon" — that gets retried every ten seconds, then
//     abandoned once five minutes past the release date makes it Shopify's
//     fault rather than a cache to wait out.
//
//   <coming-soon-form> — lives inside the fetched modal. Reads the Klaviyo
//     public ID + variant ID from data attributes, validates the form,
//     and POSTs to Klaviyo's client BIS endpoint. UI state is tracked via
//     [data-state] so CSS can swap idle/submitting/success/error views.

// setTimeout coerces its delay to a signed 32-bit int, so anything past ~24.8
// days fires immediately rather than late. Long waits are chunked instead.
const COMING_SOON_MAX_DELAY = 2147483647;
const COMING_SOON_RETRY_DELAY = 10000;
const COMING_SOON_GRACE_PERIOD = 300000;

class ComingSoonTrigger extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.handleClick.bind(this));
  }

  connectedCallback() {
    this.scheduleRefresh();
  }

  disconnectedCallback() {
    clearTimeout(this.refreshTimeout);
    this.refreshAbortController?.abort();
  }

  get availableAt() {
    const seconds = parseInt(this.dataset.availableAt, 10);
    return Number.isNaN(seconds) ? null : seconds * 1000;
  }

  get productForm() {
    return document.getElementById(`ProductForm-${this.dataset.sectionId}`);
  }

  scheduleRefresh(retryDelay) {
    clearTimeout(this.refreshTimeout);

    if (!this.dataset.sectionId || !this.dataset.productUrl) return;

    const availableAt = this.availableAt;
    if (availableAt === null) return;

    let delay = availableAt - Date.now();

    if (retryDelay !== undefined) {
      if (delay < -COMING_SOON_GRACE_PERIOD) {
        console.warn(
          '[coming-soon-trigger] Section still renders as coming soon well past the release date; giving up.'
        );
        return;
      }

      delay = retryDelay;
    }

    if (delay <= 0) {
      this.refresh();
      return;
    }

    this.refreshTimeout = setTimeout(
      () => this.scheduleRefresh(),
      Math.min(delay, COMING_SOON_MAX_DELAY)
    );
  }

  async refresh() {
    const { sectionId, productUrl, variantId } = this.dataset;

    const destination = this.productForm;
    if (!destination) return;

    this.refreshAbortController?.abort();
    this.refreshAbortController = new AbortController();

    const params = new URLSearchParams({ section_id: sectionId });
    if (variantId) params.set('variant', variantId);

    params.set('_ts', Math.floor(Date.now() / 60000));

    try {
      const response = await fetch(`${productUrl}?${params}`, {
        signal: this.refreshAbortController.signal,
      });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const source = html.getElementById(`ProductForm-${sectionId}`);
      if (!source) {
        throw new Error(`ProductForm-${sectionId} not found in response`);
      }

      const pending = source.querySelector('coming-soon-trigger');
      if (pending && pending.dataset.availableAt === this.dataset.availableAt) {
        this.scheduleRefresh(COMING_SOON_RETRY_DELAY);
        return;
      }

      const wrapper = document.createElement('div');
      HTMLUpdateUtility.setInnerHTML(wrapper, source.outerHTML);

      destination.replaceWith(wrapper.firstElementChild);
      window?.Shopify?.PaymentButton?.init();
    } catch (e) {
      if (e.name === 'AbortError') return;

      console.error('[coming-soon-trigger] Failed to refresh product form:', e);
      this.scheduleRefresh(COMING_SOON_RETRY_DELAY);
    }
  }

  async handleClick(event) {
    event.preventDefault();

    this.button = this.querySelector('button');
    if (!this.button) return;

    const productHandle = this.button.dataset.productHandle;

    if (!productHandle) return;

    this.button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=product_coming-soon`
      );
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const oldDialog = document.querySelector('#ComingSoonDialog');
      if (oldDialog) oldDialog.remove();

      const newDialog = html.querySelector('#ComingSoonDialog');
      if (!newDialog) throw new Error('Coming Soon dialog not found in response');

      document.body.appendChild(newDialog);
      newDialog.open(this.button);
    } catch (e) {
      console.error('[coming-soon-trigger] Failed to open modal:', e);
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}

customElements.define('coming-soon-trigger', ComingSoonTrigger);

class ComingSoonForm extends HTMLElement {
  constructor() {
    super();

    this.klaviyoPublicId = this.dataset.klaviyoPublicId;
    this.variantId = this.dataset.variantId;

    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('[data-field="email"]');
    this.submitButton = this.querySelector('[type="submit"]');
    this.errorEl = this.querySelector('[data-error]');
  }

  connectedCallback() {
    if (!this.form) return;
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  setState(state) {
    this.dataset.state = state;
  }

  showError(message) {
    if (this.errorEl) this.errorEl.textContent = message;
    this.setState('error');
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.klaviyoPublicId || !this.variantId) {
      this.showError('Configuration error. Please try again later.');
      return;
    }

    // Browser-native validation handles the empty/invalid-email case via
    // `required` + `type="email"` on the input. If the form got submitted,
    // we can trust the email is non-empty and well-formed.
    const email = this.emailInput.value.trim();

    const channels = ['EMAIL'];
    const profileAttributes = { email };

    this.setState('submitting');
    if (this.submitButton) this.submitButton.disabled = true;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoPublicId}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2026-04-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: profileAttributes,
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.variantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      // Klaviyo returns 202 Accepted with an empty body on success.
      if (response.status === 202) {
        this.setState('success');
        return;
      }

      // On 4xx Klaviyo usually returns a JSON:API error doc, but the body
      // can also be empty. Read as text first so .json() on an empty body
      // doesn't throw, then surface the first error detail if present.
      const responseText = await response.text();
      let detail = '';
      if (responseText) {
        try {
          detail = JSON.parse(responseText)?.errors?.[0]?.detail || '';
        } catch (e) {
          // Not JSON — fall through to generic message.
        }
      }
      this.showError(detail || this.dataset.errorGeneric || 'Something went wrong. Please try again.');
    } catch (e) {
      console.error('[coming-soon-form] Submission failed:', e);
      this.showError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
    } finally {
      if (this.submitButton) this.submitButton.disabled = false;
    }
  }
}

customElements.define('coming-soon-form', ComingSoonForm);




class RangeSlider extends HTMLElement {
  constructor() {
    super();
  }

  load() {
    this.fromSlider = this.querySelector('.range-slider__input--from');
    this.toSlider = this.querySelector('.range-slider__input--to');

    this.fromInput = this.querySelector('.range-slider__control-input--from');
    this.toInput = this.querySelector('.range-slider__control-input--to');

    if (!this.fromSlider || !this.toSlider || !this.fromInput || !this.toInput) return;

    this.fillSlider();
    this.setToggleAccessible();

    this.fromSlider.addEventListener('input', () => this.controlFromSlider());
    this.toSlider.addEventListener('input', () => this.controlToSlider());

    this.fromInput.addEventListener('input', () => this.controlFromInput());
    this.toInput.addEventListener('input', () => this.controlToInput());
  }

  fillSlider(sliderColor = '#C9C9C9', rangeColor = '#000000') {
    const rangeDistance = this.toSlider.max - this.toSlider.min;
    const fromPosition = this.fromSlider.value - this.toSlider.min;
    const toPosition = this.toSlider.value - this.toSlider.min;

    this.toSlider.style.background = `linear-gradient(
      to right,
      ${sliderColor} 0%,
      ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(toPosition / rangeDistance) * 100}%, 
      ${sliderColor} ${(toPosition / rangeDistance) * 100}%, 
      ${sliderColor} 100%)`;
  }

  controlFromSlider() {
    const fromValue = parseInt(this.fromSlider.value, 10);
    const toValue = parseInt(this.toSlider.value, 10);

    if (fromValue > toValue) {
      this.fromSlider.value = toValue;
      this.fromInput.value = toValue;
    } else {
      this.fromInput.value = fromValue;
    }

    this.fillSlider();
  }

  controlFromInput() {
    const fromValue = parseInt(this.fromInput.value, 10);
    const toValue = parseInt(this.toInput.value, 10);

    if (fromValue > toValue) {
      this.fromSlider.value = toValue;
      this.fromInput.value = toValue;
    } else {
      this.fromInput.value = fromValue;
    }

    this.fillSlider();
  }

  controlToSlider() {
    const fromValue = parseInt(this.fromSlider.value, 10);
    const toValue = parseInt(this.toSlider.value, 10);

    if (fromValue <= toValue) {
      this.toSlider.value = toValue;
      this.toInput.value = toValue;
    } else {
      this.toInput.value = fromValue;
      this.toSlider.value = fromValue;
    }

    this.fillSlider();
    this.setToggleAccessible();
  }

  controlToInput() {
    const fromValue = parseInt(this.fromInput.value, 10);
    const toValue = parseInt(this.toInput.value, 10);

    if (fromValue <= toValue) {
      this.toSlider.value = toValue;
      this.toInput.value = toValue;
    } else {
      this.toInput.value = fromValue;
      this.toSlider.value = fromValue;
    }

    this.fillSlider();
    this.setToggleAccessible();
  }

  setToggleAccessible() {
    if (Number(this.toSlider.value) <= 0) {
      this.toSlider.style.zIndex = 2;
    } else {
      this.toSlider.style.zIndex = 0;
    }
  }
}

customElements.define('range-slider', RangeSlider);

// Click-to-open tooltip anchored to a small icon. Used e.g. next to the
// "pay later" price on the PDP. Markup contract:
//   <highlight-component>
//     <span class="highlight-icon">ⓘ</span>
//     <div class="highlight-tooltip">…</div>
//   </highlight-component>
// The element enhances the icon into a keyboard/screen-reader-accessible
// button, wires open/close on click and Escape, and closes on outside
// click. Position/triangle handled by CSS.
class HighlightComponent extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.highlight-icon');
    this.tooltip = this.querySelector('.highlight-tooltip');
    this.open = false;
  }

  connectedCallback() {
    if (!this.icon || !this.tooltip) return;

    // Give the tooltip an id we can associate with the trigger. Only mint
    // one if the markup didn't already provide it.
    if (!this.tooltip.id) {
      HighlightComponent.instanceCount = (HighlightComponent.instanceCount || 0) + 1;
      this.tooltip.id = `highlight-tooltip-${HighlightComponent.instanceCount}`;
    }
    this.tooltip.setAttribute('role', 'tooltip');
    // Hidden from AT when closed; JS toggles this in sync with .is-open.
    // display:none already hides it, but this belt-and-suspenders keeps
    // aria-describedby from announcing content that isn't visible.
    this.tooltip.setAttribute('aria-hidden', 'true');

    // Enhance the icon (usually a <span>) into a real, keyboard- and
    // screen-reader-accessible control.
    //  - role="button" makes AT announce it as a button, not text
    //  - tabindex="0" puts it in the natural tab order
    //  - aria-expanded communicates open/closed state
    //  - aria-controls links to the tooltip element structurally
    //  - aria-describedby makes SRs announce the tooltip content when
    //    focus lands on the trigger (once the tooltip is visible)
    //  - aria-label describes the icon since its glyph (ⓘ) reads poorly
    this.icon.setAttribute('role', 'button');
    this.icon.setAttribute('tabindex', '0');
    this.icon.setAttribute('aria-expanded', 'false');
    this.icon.setAttribute('aria-controls', this.tooltip.id);
    this.icon.setAttribute('aria-describedby', this.tooltip.id);
    if (!this.icon.hasAttribute('aria-label')) {
      this.icon.setAttribute('aria-label', 'More information');
    }

    this.boundOnIconClick = this.onIconClick.bind(this);
    this.boundOnIconKeydown = this.onIconKeydown.bind(this);
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.boundOnViewportChange = this.onViewportChange.bind(this);

    this.icon.addEventListener('click', this.boundOnIconClick);
    this.icon.addEventListener('keydown', this.boundOnIconKeydown);

    this.setOpen(false);
  }

  disconnectedCallback() {
    this.icon?.removeEventListener('click', this.boundOnIconClick);
    this.icon?.removeEventListener('keydown', this.boundOnIconKeydown);
    document.removeEventListener('click', this.boundOnDocumentClick);
    document.removeEventListener('keydown', this.boundOnDocumentKeydown);
    window.removeEventListener('scroll', this.boundOnViewportChange, true);
    window.removeEventListener('resize', this.boundOnViewportChange);
  }

  onIconClick(event) {
    event.stopPropagation();
    this.setOpen(!this.open);
  }

  onIconKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setOpen(!this.open);
    }
  }

  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.setOpen(false);
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      this.setOpen(false);
      this.icon.focus();
    }
  }

  setOpen(open) {
    this.open = open;
    this.classList.toggle('is-open', open);
    this.icon.setAttribute('aria-expanded', String(open));
    this.tooltip.setAttribute('aria-hidden', String(!open));

    if (open) {
      // Position via JS with viewport clamping — the CSS-only
      // `left: 50%; translateX(-50%)` centered on the icon doesn't work when
      // the icon is near a screen edge (tooltip falls off).
      this.positionTooltip();

      // Defer the listener registration by a tick so the click that opened
      // us doesn't immediately close us via the outside-click handler.
      requestAnimationFrame(() => {
        document.addEventListener('click', this.boundOnDocumentClick);
        document.addEventListener('keydown', this.boundOnDocumentKeydown);
      });

      // Scroll/resize while open would misalign the fixed-position tooltip
      // relative to the (moving) icon. Simpler to close on either.
      window.addEventListener('scroll', this.boundOnViewportChange, true);
      window.addEventListener('resize', this.boundOnViewportChange);
    } else {
      document.removeEventListener('click', this.boundOnDocumentClick);
      document.removeEventListener('keydown', this.boundOnDocumentKeydown);
      window.removeEventListener('scroll', this.boundOnViewportChange, true);
      window.removeEventListener('resize', this.boundOnViewportChange);

      // Clear inline positioning so it doesn't leak between opens.
      this.tooltip.style.top = '';
      this.tooltip.style.left = '';
      this.tooltip.style.right = '';
      this.tooltip.style.transform = '';
      this.tooltip.style.position = '';
      this.tooltip.style.removeProperty('--triangle-x');
    }
  }

  positionTooltip() {
    const iconRect = this.icon.getBoundingClientRect();
    const margin = 16; // minimum breathing room from viewport edges
    const gap = 10; // vertical gap between icon and tooltip

    // Switch to fixed positioning so we can clamp against the viewport
    // regardless of where the component sits in the DOM.
    this.tooltip.style.position = 'fixed';
    this.tooltip.style.transform = 'none';
    this.tooltip.style.left = '0';
    this.tooltip.style.top = `${iconRect.bottom + gap}px`;

    // Measure the tooltip once it's laid out at 0,0 with its natural width.
    // offsetWidth respects the max-width from CSS.
    const tooltipWidth = this.tooltip.offsetWidth;
    const viewportWidth = window.innerWidth;

    const iconCenterX = iconRect.left + iconRect.width / 2;
    let tooltipLeft = iconCenterX - tooltipWidth / 2;

    // Clamp so the tooltip stays fully in-viewport with margin on both sides.
    const minLeft = margin;
    const maxLeft = viewportWidth - tooltipWidth - margin;
    tooltipLeft = Math.max(minLeft, Math.min(maxLeft, tooltipLeft));

    this.tooltip.style.left = `${tooltipLeft}px`;

    // Slide the triangle so it still points at the icon center. Value is
    // consumed by the CSS ::before / ::after rules via var(--triangle-x).
    const triangleX = iconCenterX - tooltipLeft;
    this.tooltip.style.setProperty('--triangle-x', `${triangleX}px`);
  }

  onViewportChange() {
    // Simpler than repositioning: close on scroll/resize. The user can
    // tap the icon again after they're done scrolling.
    this.setOpen(false);
  }
}

customElements.define('highlight-component', HighlightComponent);

class AnnouncementSlider extends HTMLElement {
  constructor() {
    super();

    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.visibleSlides = [];
    this.intervalId = null;
    this.paused = false;
    this.current = 0;
    this.interval = Number(this.dataset.interval) || 9000;
    this.desktopMedia = window.matchMedia('(min-width: 750px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    if (this.slides.length === 0) return;

    this.boundMediaChange = this.handleMediaChange.bind(this);
    this.desktopMedia.addEventListener('change', this.boundMediaChange);

    this.updateVisibleSlides();

    if (this.reducedMotion) return;

    this.boundPause = this.pause.bind(this);
    this.boundResume = this.resume.bind(this);
    this.boundFocusOut = this.handleFocusOut.bind(this);

    this.addEventListener('mouseenter', this.boundPause);
    this.addEventListener('mouseleave', this.boundResume);
    this.addEventListener('focusin', this.boundPause);
    this.addEventListener('focusout', this.boundFocusOut);
  }

  disconnectedCallback() {
    this.stop();
    if (this.boundMediaChange) {
      this.desktopMedia.removeEventListener('change', this.boundMediaChange);
    }
    if (this.boundPause) {
      this.removeEventListener('mouseenter', this.boundPause);
      this.removeEventListener('mouseleave', this.boundResume);
      this.removeEventListener('focusin', this.boundPause);
      this.removeEventListener('focusout', this.boundFocusOut);
    }
  }

  isSlideVisible(slide) {
    const isDesktop = this.desktopMedia.matches;
    if (isDesktop && slide.classList.contains('hidden--desktop')) return false;
    if (!isDesktop && slide.classList.contains('hidden--mobile')) return false;
    return true;
  }

  updateVisibleSlides() {
    this.visibleSlides = this.slides.filter((slide) => this.isSlideVisible(slide));

    this.slides.forEach((slide) => {
      slide.classList.remove('is-active');
      slide.setAttribute('aria-hidden', 'true');
    });

    this.current = 0;

    if (this.visibleSlides.length > 0) {
      this.visibleSlides[0].classList.add('is-active');
      this.visibleSlides[0].removeAttribute('aria-hidden');
    }

    this.stop();
    if (!this.reducedMotion && !this.paused && this.visibleSlides.length > 1) {
      this.start();
    }
  }

  handleMediaChange() {
    this.updateVisibleSlides();
  }

  advance() {
    if (this.visibleSlides.length <= 1) return;
    this.visibleSlides[this.current].classList.remove('is-active');
    this.visibleSlides[this.current].setAttribute('aria-hidden', 'true');
    this.current = (this.current + 1) % this.visibleSlides.length;
    this.visibleSlides[this.current].classList.add('is-active');
    this.visibleSlides[this.current].removeAttribute('aria-hidden');
  }

  start() {
    if (this.intervalId === null && !this.paused && this.visibleSlides.length > 1) {
      this.intervalId = setInterval(this.advance.bind(this), this.interval);
    }
  }

  stop() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  pause() {
    this.paused = true;
    this.stop();
  }

  resume() {
    this.paused = false;
    if (this.visibleSlides.length > 1) this.start();
  }

  handleFocusOut(e) {
    if (!this.contains(e.relatedTarget)) this.resume();
  }
}

customElements.define('announcement-slider', AnnouncementSlider);